import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ApiTestModal } from './ApiTestModal';
import { CreateOrderTestModal } from './CreateOrderTestModal';
import { VoiceConfigTestModal } from './VoiceConfigTestModal';
import { NumberAssignTestModal } from './NumberAssignTestModal';
import { ApiKeyDisplay } from './ApiKeyDisplay';
import { useAppSelector } from '../../redux/hooks';

interface Parameter {
  name: string;
  type: string;
  required: boolean;
  description: string;
  options?: string[];
  example?: string | any;
  availablePlaceholders?: Array<{ value: string; label: string }>;
}

interface ResponseExample {
  description: string;
  example: any;
  field_descriptions?: {
    title: string;
    categories: Array<{
      name: string;
      fields: Array<{
        name: string;
        description: string;
      }>;
    }>;
  };
}

interface ApiEndpointData {
  title: string;
  method: string;
  endpoint: string;
  description: string;
  parameters?: {
    body?: Parameter[];
    query?: Parameter[];
    header?: Parameter[];
  };
  responses: {
    [key: string]: ResponseExample;
  };
  authentication?: any;
  sample_response?: any;
  field_descriptions?: any;
  curl_example?: any;
  curlExample?: string;
  curlExampleDescription?: string;
}

interface ApiEndpointDisplayProps {
  data: ApiEndpointData;
}

export const ApiEndpointDisplay: React.FC<ApiEndpointDisplayProps> = ({ data }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isCreateOrderModalOpen, setIsCreateOrderModalOpen] = useState(false);
  const [isVoiceConfigModalOpen, setIsVoiceConfigModalOpen] = useState(false);
  const [isNumberAssignModalOpen, setIsNumberAssignModalOpen] = useState(false);
  const navigate = useNavigate();
  const { apiBaseUrl, currentApiKey } = useAppSelector((state) => state.auth);
  const { selectedRestaurantId } = useAppSelector((state) => state.restaurant);

  const handleEndpointChange = (newEndpointTitle: string) => {
    // Map endpoint titles to routes
    const endpointRoutes: Record<string, string> = {
      'Restaurant Account API Overview': '/docs/api-reference/menubuilder/register',
      'Upload Menu': '/docs/api-reference/menubuilder/upload',
      'Get Menu Items': '/docs/api-reference/menubuilder/getmenu',
      'Get Status': '/docs/api-reference/menubuilder/getstatus'
    };

    const route = endpointRoutes[newEndpointTitle];
    if (route) {
      navigate(route);
    }
  };

  const getMethodBadgeStyles = (method: string) => {
    const baseStyles = 'inline-flex items-center px-3 py-1 rounded text-sm font-semibold uppercase tracking-wide';
    
    switch (method.toUpperCase()) {
      case 'GET':
        return `${baseStyles} bg-green-100 text-green-800`;
      case 'POST':
        return `${baseStyles} bg-blue-100 text-blue-800`;
      case 'PUT':
        return `${baseStyles} bg-orange-100 text-orange-800`;
      case 'PATCH':
        return `${baseStyles} bg-purple-100 text-purple-800`;
      case 'DELETE':
        return `${baseStyles} bg-red-100 text-red-800`;
      default:
        return `${baseStyles} bg-gray-100 text-gray-800`;
    }
  };

  const generateCurlCommand = () => {
    // Remove leading '/api/menu-builder' from endpoint if it exists, since apiBaseUrl already includes it
    const cleanEndpoint = data.endpoint.replace(/^\/api\/menu-builder/, '');
    
    // Special handling for Create Order endpoint
    if (data.title === 'Create Order') {
      return `curl --request ${data.method} \\
  --url ${apiBaseUrl}${cleanEndpoint} \\
  --header 'API-Key: <account-id>' \\
  --header 'Content-Type: application/json' \\
  --data '{
    "name": "<customer_name>",
    "phone": "<customer_phone>",
    "email": "<customer_email>",
    "specialRequests": "<special_instructions>",
    "orderedItems": [
      {
        "name": "<item_name>",
        "quantity": "<quantity>",
        "itemPrice": "<item_price>",
        "modifiers": [
          {
            "modifier_name": "<modifier_name>",
            "modifier_price": "<modifier_price>"
          }
        ]
      }
    ]
  }'`;
    }
    
    // Default handling for other endpoints
    return `curl --request ${data.method} \\
  --url ${apiBaseUrl}${cleanEndpoint} \\
  --header 'API-Key: <account-id>'${data.parameters?.body ? ` \\
  --header 'Content-Type: application/json' \\
  --data '{
${data.parameters.body.map(param => `    "${param.name}": "${param.type === 'string' ? 'example' : 'value'}"`).join(',\n')}
  }'` : ''}`;
  };

  const getCurlCommand = () => {
    // Use curlExample from JSON if available (with actual data)
    if (data.curlExample) {
      // Replace hardcoded URL with Redux apiBaseUrl
      let curl = data.curlExample.replace(
        /https:\/\/sandboxmenubuilder\.snapmenu\.ai\/api\/menu-builder/g,
        apiBaseUrl
      );
      
      // Replace YOUR_API_KEY placeholder with actual API key if available
      if (currentApiKey) {
        curl = curl.replace(/YOUR_API_KEY/g, currentApiKey);
      }
      
      return curl;
    }
    // Otherwise generate it
    return generateCurlCommand();
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  return (
    <div className="flex h-full">
      {/* Main Content Area - API Documentation */}
      <div className="flex-1 pr-4 overflow-y-auto">
        {/* Page Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-4">              
              <span className={getMethodBadgeStyles(data.method)}>
                {data.method}
              </span>
              <h1 className="text-2xl font-bold text-[var(--text-primary)]">
                {data.title}
              </h1>
            </div>
            <button 
              onClick={() => {
                // Open NumberAssignTestModal for Number Assign and Get Available Numbers endpoints
                if (data.title === 'Assign Number to Order' || data.title === 'Get Available Numbers') {
                  setIsNumberAssignModalOpen(true);
                }
                // Open VoiceConfigTestModal for Voice Configuration endpoints (including unified endpoint)
                else if (data.title === 'Voice Configuration' || data.title === 'Get Voice Configuration' || data.title === 'Update Voice Configuration' || data.title === 'Create Voice Configuration') {
                  setIsVoiceConfigModalOpen(true);
                }
                // Open CreateOrderTestModal for Create Order endpoint
                else if (data.title === 'Create Order') {
                  setIsCreateOrderModalOpen(true);
                } 
                // Open generic ApiTestModal for all other endpoints (including Register Restaurants)
                else {
                  setIsModalOpen(true);
                }
              }}
              className="px-4 py-2 bg-[var(--accent-color)] text-white rounded-lg hover:opacity-90 transition-colors flex items-center"
            >
              <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 24 24">
                <path d="M8 5v14l11-7z"/>
              </svg>
              Try it
            </button>
          </div>
          
          <p className="text-[var(--text-secondary)] text-lg mb-6">
            {data.description}
          </p>

          {/* Endpoint URL */}
          <div className="bg-[var(--bg-secondary)] rounded-lg p-4 border border-[var(--border-color)] mb-4">
            <div className="flex items-center space-x-3">
              <span className={getMethodBadgeStyles(data.method)}>
                {data.method}
              </span>
              <code className="text-[var(--text-primary)] font-mono text-lg font-semibold">
                /{data.endpoint.split('/').filter(Boolean).join('/')}
              </code>
            </div>
          </div>
        </div>

        {/* API Documentation Sections */}
        <div className="space-y-8">
          {/* Authorization Section */}
          <section>
            <h2 className="text-xl font-bold text-[var(--text-primary)] mb-6">
              Authorizations
            </h2>
            
            {data.authentication?.required_headers ? (
              <div className="space-y-4">
                {data.authentication.required_headers.map((header: any, index: number) => (
                  <div key={index} className="border-l-4 border-[var(--accent-color)] pl-4">
                    <div className="flex items-center space-x-2 mb-2">
                      <h3 className="text-lg font-semibold text-[var(--accent-color)]">
                        {header.name}
                      </h3>
                    </div>
                    <div className="flex items-center space-x-4 text-sm mb-2">
                      <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded">string</span>
                      <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded">header</span>
                      <span className="px-2 py-1 bg-red-100 text-red-700 rounded font-semibold">required</span>
                    </div>
                    
                    {/* Display actual API Key ONLY under the API-Key header (case-insensitive) */}
                    {header.name.toLowerCase() === 'api-key' && <ApiKeyDisplay />}
                    
                    <p className="text-[var(--text-secondary)] mt-2">{header.description}</p>
                  </div>
                ))}
              </div>
            ) : data.parameters?.header ? (
              <div className="space-y-4">
                {data.parameters.header.map((header: any, index: number) => (
                  <div key={index} className="border-l-4 border-[var(--accent-color)] pl-4">
                    <div className="flex items-center space-x-2 mb-2">
                      <h3 className="text-lg font-semibold text-[var(--accent-color)]">
                        {header.name}
                      </h3>
                    </div>
                    <div className="flex items-center space-x-4 text-sm mb-2">
                      <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded">{header.type || 'string'}</span>
                      <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded">header</span>
                      <span className={`px-2 py-1 rounded font-semibold ${
                        header.required 
                          ? 'bg-red-100 text-red-700' 
                          : 'bg-gray-100 text-gray-700'
                      }`}>
                        {header.required ? 'required' : 'optional'}
                      </span>
                    </div>
                    
                    {/* Display actual API Key ONLY under the API-Key header (case-insensitive) */}
                    {header.name.toLowerCase() === 'api-key' && <ApiKeyDisplay />}
                    
                    <p className="text-[var(--text-secondary)] mt-2">{header.description}</p>
                  </div>
                ))}
              </div>
            ) : (
              <div className="space-y-4">
                <div className="border-l-4 border-[var(--accent-color)] pl-4">
                  <div className="flex items-center space-x-2 mb-2">
                    <h3 className="text-lg font-semibold text-[var(--accent-color)]">
                      API-Key
                    </h3>
                  </div>
                  <div className="flex items-center space-x-4 text-sm mb-2">
                    <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded">string</span>
                    <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded">header</span>
                    <span className="px-2 py-1 bg-red-100 text-red-700 rounded font-semibold">required</span>
                  </div>
                  
                  {/* Display actual API Key using reusable component */}
                  <ApiKeyDisplay />
                  
                  <p className="text-[var(--text-secondary)] mt-2">API key</p>
                </div>
              </div>
            )}
          </section>

          {/* Parameters Section */}
          {(data.parameters?.body || data.parameters?.query) && (
            <section>
              <h2 className="text-xl font-bold text-[var(--text-primary)] mb-6">
                Parameters
              </h2>
              
              {data.parameters.query && (
                <div className="mb-8">
                  <h3 className="text-lg font-semibold text-[var(--text-primary)] mb-4">Query Parameters</h3>
                  <div className="space-y-6">
                    {data.parameters.query.map((param, index) => (
                      <div key={index} className="border-l-4 border-[var(--accent-color)] pl-4">
                        <div className="flex items-center space-x-2 mb-2">
                          <h4 className="text-base font-semibold text-[var(--accent-color)]">
                            {param.name}
                          </h4>
                        </div>
                        <div className="flex items-center space-x-4 text-sm mb-2">
                          <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded">{param.type}</span>
                          <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded">query</span>
                          <span className={`px-2 py-1 rounded font-semibold ${
                            param.required 
                              ? 'bg-red-100 text-red-700' 
                              : 'bg-gray-100 text-gray-700'
                          }`}>
                            {param.required ? 'required' : 'optional'}
                          </span>
                        </div>
                        {param.options && param.options.length > 0 && (
                          <div className="mb-2 flex items-center flex-wrap gap-2">
                            <span className="text-sm font-medium text-[var(--text-muted)]">Options:</span>
                            {param.options.map((option, optIndex) => (
                              <span key={optIndex} className="px-2 py-1 bg-blue-50 text-blue-700 rounded text-xs font-medium">
                                {option}
                              </span>
                            ))}
                          </div>
                        )}
                        <p className="text-[var(--text-secondary)]">{param.description}</p>
                        
                        {/* Display Example if available */}
                        {param.example && typeof param.example === 'string' && (
                          <div className="mt-3 bg-gray-50 border-l-4 border-gray-300 p-3 rounded">
                            <p className="text-sm font-medium text-gray-700 mb-1">Example:</p>
                            <code className="text-sm text-gray-800 font-mono">{param.example}</code>
                          </div>
                        )}
                        
                        {/* Display Available Placeholders if available */}
                        {param.availablePlaceholders && param.availablePlaceholders.length > 0 && (
                          <div className="mt-3 bg-gray-50 border-l-4 border-gray-300 p-3 rounded">
                            <p className="text-sm font-medium text-gray-700 mb-2">Placeholders:</p>
                            <div className="flex flex-wrap gap-2">
                              {param.availablePlaceholders.map((placeholder, phIndex) => (
                                <button
                                  key={phIndex}
                                  onClick={() => copyToClipboard(placeholder.value)}
                                  className="px-3 py-1.5 bg-white border border-gray-300 rounded-md hover:bg-gray-100 transition-colors flex items-center space-x-2"
                                  title={`Click to copy: ${placeholder.value}`}
                                >
                                  <code className="text-xs font-mono text-gray-800">{placeholder.value}</code>
                                  <span className="text-xs text-gray-600">({placeholder.label})</span>
                                </button>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {data.parameters.body && (
                <div>
                  <h3 className="text-lg font-semibold text-[var(--text-primary)] mb-4">Body Parameters</h3>
                  <div className="space-y-6">
                    {data.parameters.body.map((param, index) => (
                      <div key={index} className="border-l-4 border-[var(--accent-color)] pl-4">
                        <div className="flex items-center space-x-2 mb-2">
                          <h4 className="text-base font-semibold text-[var(--accent-color)]">
                            {param.name}
                          </h4>
                        </div>
                        <div className="flex items-center space-x-4 text-sm mb-2">
                          <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded">{param.type}</span>
                          <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded">body</span>
                          <span className={`px-2 py-1 rounded font-semibold ${
                            param.required 
                              ? 'bg-red-100 text-red-700' 
                              : 'bg-gray-100 text-gray-700'
                          }`}>
                            {param.required ? 'required' : 'optional'}
                          </span>
                        </div>
                        {param.options && param.options.length > 0 && (
                          <div className="mb-2 flex items-center flex-wrap gap-2">
                            <span className="text-sm font-medium text-[var(--text-muted)]">Options:</span>
                            {param.options.map((option, optIndex) => (
                              <span key={optIndex} className="px-2 py-1 bg-blue-50 text-blue-700 rounded text-xs font-medium">
                                {option}
                              </span>
                            ))}
                          </div>
                        )}
                        <p className="text-[var(--text-secondary)]">{param.description}</p>
                        
                        {/* Display Example if available */}
                        {param.example && typeof param.example === 'string' && (
                          <div className="mt-3 bg-gray-50 border-l-4 border-gray-300 p-3 rounded">
                            <p className="text-sm font-medium text-gray-700 mb-1">Example:</p>
                            <code className="text-sm text-gray-800 font-mono">{param.example}</code>
                          </div>
                        )}
                        
                        {/* Display Available Placeholders if available */}
                        {param.availablePlaceholders && param.availablePlaceholders.length > 0 && (
                          <div className="mt-3 bg-gray-50 border-l-4 border-gray-300 p-3 rounded">
                            <p className="text-sm font-medium text-gray-700 mb-2">Placeholders:</p>
                            <div className="flex flex-wrap gap-2">
                              {param.availablePlaceholders.map((placeholder, phIndex) => (
                                <button
                                  key={phIndex}
                                  onClick={() => copyToClipboard(placeholder.value)}
                                  className="px-3 py-1.5 bg-white border border-gray-300 rounded-md hover:bg-gray-100 transition-colors flex items-center space-x-2"
                                  title={`Click to copy: ${placeholder.value}`}
                                >
                                  <code className="text-xs font-mono text-gray-800">{placeholder.value}</code>
                                  <span className="text-xs text-gray-600">({placeholder.label})</span>
                                </button>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </section>
          )}

          {/* Sample Response Section */}
          {data.sample_response && (
            <section>
              <h2 className="text-xl font-bold text-[var(--text-primary)] mb-6">
                {data.sample_response.title}
              </h2>
              <div className="bg-[var(--bg-secondary)] rounded-lg border border-[var(--border-color)] p-4 mb-6">
                <pre className="text-sm text-[var(--text-primary)] font-mono overflow-x-auto whitespace-pre-wrap">
                  <code>{JSON.stringify(data.sample_response.example, null, 2)}</code>
                </pre>
              </div>
            </section>
          )}

          {/* Field Descriptions Section */}
          {data.field_descriptions && (
            <section>
              <h2 className="text-xl font-bold text-[var(--text-primary)] mb-6">
                {data.field_descriptions.title}
              </h2>
              
              <div className="space-y-8">
                {data.field_descriptions.categories.map((category: any, categoryIndex: number) => (
                  <div key={categoryIndex}>
                    <h3 className="text-lg font-semibold text-[var(--text-primary)] mb-4">
                      {category.name}
                    </h3>
                    <div className="space-y-4">
                      {category.fields.map((field: any, fieldIndex: number) => (
                        <div key={fieldIndex} className="border-l-4 border-[var(--accent-color)] pl-4">
                          <div className="flex items-center space-x-2 mb-2">
                            <h4 className="text-base font-semibold text-[var(--accent-color)]">
                              {field.name}
                            </h4>
                          </div>
                          <p className="text-[var(--text-secondary)]">{field.description}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Response Section */}
          <section>
            <h2 className="text-xl font-bold text-[var(--text-primary)] mb-6">
              Response
            </h2>
            
            {Object.entries(data.responses).map(([statusCode, response]) => (
              <div key={statusCode} className="mb-8">
                <div className="flex items-center space-x-3 mb-6">
                  <span className={`text-lg font-bold ${
                    parseInt(statusCode) >= 200 && parseInt(statusCode) < 300 
                      ? 'text-green-600' 
                      : 'text-red-600'
                  }`}>
                    {statusCode}
                  </span>
                  <span className="text-[var(--text-secondary)]">- application/json</span>
                </div>
                <p className="text-[var(--text-secondary)] mb-4">{response.description}</p>
                
                {/* Field Descriptions within Response */}
                {response.field_descriptions && (
                  <div className="mb-6 space-y-6">
                    {response.field_descriptions.categories.map((category: any, categoryIndex: number) => (
                      <div key={categoryIndex}>
                        <h4 className="text-base font-semibold text-[var(--text-primary)] mb-3">
                          {category.name}
                        </h4>
                        <div className="space-y-3">
                          {category.fields.map((field: any, fieldIndex: number) => (
                            <div key={fieldIndex} className="border-l-4 border-[var(--accent-color)] pl-4">
                              <div className="flex items-center space-x-2 mb-1">
                                <h5 className="text-sm font-semibold text-[var(--accent-color)]">
                                  {field.name}
                                </h5>
                              </div>
                              <p className="text-sm text-[var(--text-secondary)]">{field.description}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                
                <div className="bg-[var(--bg-secondary)] rounded-lg border border-[var(--border-color)] p-4">
                  <pre className="text-sm text-[var(--text-primary)] font-mono overflow-x-auto whitespace-pre-wrap">
                    <code>{JSON.stringify(response.example, null, 2)}</code>
                  </pre>
                </div>
              </div>
            ))}
          </section>
        </div>
      </div>

      {/* Right Column - Fixed Code Examples Panel */}
      <div className="w-96 flex-shrink-0 bg-[var(--bg-secondary)] border-l border-[var(--border-color)] h-full overflow-y-auto">
        <div className="p-4 space-y-4">
          {/* cURL Header */}
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-[var(--text-primary)]">cURL</h3>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => copyToClipboard(getCurlCommand())}
                className="text-[var(--text-secondary)] hover:text-[var(--accent-color)] transition-colors p-1"
                title="Copy"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
              </button>
            </div>
          </div>

          {/* cURL Code Block */}
          <div className="bg-[var(--bg-tertiary)] rounded-lg border border-[var(--border-color)] p-4 mb-6">
            <pre className="text-sm text-[var(--text-primary)] font-mono overflow-x-auto">
              <code>{getCurlCommand()}</code>
            </pre>
          </div>

          {/* cURL Example Description */}
          {data.curlExampleDescription && (
            <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-6">
              <p className="text-sm text-gray-700 leading-relaxed">
                {data.curlExampleDescription}
              </p>
            </div>
          )}

          {/* Response Examples */}
          {Object.entries(data.responses).map(([statusCode, response]) => (
            <div key={statusCode} className="mb-6">
              <div className="flex items-center justify-between mb-2">
                <span className={`font-bold text-lg ${
                  parseInt(statusCode) >= 200 && parseInt(statusCode) < 300 
                    ? 'text-green-600' 
                    : 'text-red-600'
                }`}>
                  {statusCode}
                </span>
                <button
                  onClick={() => copyToClipboard(JSON.stringify(response.example, null, 2))}
                  className="text-[var(--text-secondary)] hover:text-[var(--accent-color)] transition-colors p-1"
                  title="Copy"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                </button>
              </div>
              <div className="bg-[var(--bg-tertiary)] rounded-lg border border-[var(--border-color)] p-4">
                <pre className="text-sm text-[var(--text-primary)] font-mono overflow-x-auto whitespace-pre-wrap">
                  <code>{JSON.stringify(response.example, null, 2)}</code>
                </pre>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* API Test Modal */}
      <ApiTestModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        endpoint={{
          title: data.title,
          method: data.method,
          endpoint: data.endpoint,
          parameters: data.parameters
        }}
        onEndpointChange={handleEndpointChange}
      />

      {/* Create Order Test Modal */}
      <CreateOrderTestModal
        isOpen={isCreateOrderModalOpen}
        onClose={() => setIsCreateOrderModalOpen(false)}
        restaurantId={selectedRestaurantId || ''}
        apiKey={currentApiKey || ''}
      />

      {/* Voice Configuration Test Modal */}
      <VoiceConfigTestModal
        isOpen={isVoiceConfigModalOpen}
        onClose={() => setIsVoiceConfigModalOpen(false)}
        endpoint={{
          title: data.title,
          method: data.method,
          endpoint: data.endpoint,
          parameters: data.parameters
        }}
      />

      {/* Number Assign Test Modal */}
      <NumberAssignTestModal
        isOpen={isNumberAssignModalOpen}
        onClose={() => setIsNumberAssignModalOpen(false)}
        endpoint={{
          title: data.title,
          method: data.method,
          endpoint: data.endpoint,
          parameters: data.parameters
        }}
      />
    </div>
  );
};
